import router from "@/router";

// Palabras clave que identifican un 401 genuino de expiración/invalidez de token.
// Cualquier otro 401 (p.ej. acceso denegado a un recurso específico) NO debe
// cerrar la sesión del usuario.
const TOKEN_EXPIRY_KEYWORDS = [
    'unauthenticated',
    'token has expired',
    'token is expired',
    'token expired',
    'expired token',
    'token invalid',
    'invalid token',
    'token is invalid',
    'token not provided',
    'session has expired',
    'session expired',
    'sesión ha expirado',
    'sesion ha expirado',
]

function isTokenExpiry(message) {
    if (!message || message.trim() === '') return false
    const lower = message.toLowerCase()
    return TOKEN_EXPIRY_KEYWORDS.some(keyword => lower.includes(keyword))
}

//Decode error from API when return a bad request
export function errorHttpMessage(http_request) {

    // Se lee con `?.`: quien llama lo hace desde el catch de cada acción del store, donde
    // un throw ya no tiene red debajo. Un error sin objeto (undefined/null) cae en la rama de
    // red de abajo en vez de reventar aquí.
    const response = http_request?.response;

    if (response != undefined) {

        const error_status = response.status;

        if (error_status == 422) {
            let res
            try { res = JSON.parse(response.request.response) } catch (_) {
                return {"message": "No hemos podido procesar tu solicitud, inténtalo nuevamente.", "error_keys": null}
            }
            //Laravel error request
            if (res?.errors) {

                let errors = Object.keys(res.errors);
                let empty = [];
                let error_keys = [];
                let subtexts = '';
                errors.forEach((element) => {
                    let filteredValues = res.errors[element];
                    error_keys.push(element);
                    filteredValues.forEach((sub_element) => {
                        empty.push(`<li class="li_details_error">${sub_element}</li>`)
                    });
                    return empty;
                });
                subtexts = empty.join("");
                return {"message": subtexts, "error_keys": error_keys};
            } else {
                console.log(res)
                // Un 422 sin `errors` ni envelope `result` (p. ej. un cuerpo JSON de otra forma)
                // reventaba al leer `res.result.message` dentro del catch del store y el
                // usuario se quedaba con un modal en blanco.
                return {"message": res?.result?.message || "No hemos podido procesar tu solicitud, inténtalo nuevamente.", "error_keys": null};
            }

        } else if (error_status == 401) {

            let res
            try { res = JSON.parse(response.request.response) } catch (_) {
                return {"message": "", "error_keys": null}
            }
            const messsage = res.result?.["message"] ?? ""

            // Solo limpiar la sesión y redirigir cuando el 401 indica realmente
            // que el token ha expirado o es inválido. Otros 401 (p.ej. acceso
            // denegado a un recurso) solo devuelven el mensaje de error.
            if (isTokenExpiry(messsage)) {
                sessionStorage.clear()

                // Persistir el mensaje de expiración para mostrarlo en la pantalla de login
                if (messsage) {
                    sessionStorage.setItem('__session_expired_msg', messsage)
                }

                // Diferir la navegación al siguiente tick para no interferir con el
                // guard de ruta que puede estar procesando el mismo 401 en ese momento.
                // Si el guard ya redirigió a login, este push es un no-op.
                setTimeout(() => router.push({name: 'auth-login'}), 0)
            }

            return {"message": messsage, "error_keys": null};

        } else if (error_status == 429) {
            // Bloqueo temporal: el backend devuelve el mensaje ya traducido
            // y los minutos restantes para que el frontend pueda mostrar un contador.
            let res
            try { res = JSON.parse(response.request.response) } catch (_) {
                return {"message": "Has excedido el número de intentos permitidos. Tu acceso está bloqueado temporalmente.", "error_keys": null, "lockout_type": "temporary", "lockout_minutes": null}
            }
            return {
                "message": res.result?.message ?? "Has excedido el número de intentos permitidos. Tu acceso está bloqueado temporalmente.",
                "error_keys": null,
                "lockout_type": "temporary",
                "lockout_minutes": res.result?.lockout_minutes_remaining ?? null,
            };

        } else if (error_status == 400) {
            // Errores de dominio del login (cuenta inactiva, sin grupo, etc.):
            // el backend los lanza como \Exception genérica y responde 400 con
            // el mensaje real en result.message. Sin este caso, caían en el
            // "else" y siempre se mostraba el mensaje genérico.
            let res
            try { res = JSON.parse(response.request.response) } catch (_) {
                return {"message": "No hemos podido procesar tu solicitud, inténtalo nuevamente.", "error_keys": null}
            }
            return {"message": res.result?.message ?? "No hemos podido procesar tu solicitud, inténtalo nuevamente.", "error_keys": null};

        } else if (error_status == 403) {
            // HCSO-2870: el candado de altas del expediente (colaborador Inactivo / Baja)
            // responde 403 con envelope Sherpa `{result:{error,message,data}}`. Se pinta
            // result.message; NUNCA res.message (los 403 de stock de Laravel traen
            // `{message: "This action is unauthorized."}`, en inglés y sin contexto).
            // `||` y no `??`: un message vacío también cae al genérico.
            let res
            try { res = JSON.parse(response.request.response) } catch (_) { res = null }
            return {"message": res?.result?.message || "No hemos podido procesar tu solicitud, inténtalo nuevamente.", "error_keys": null};

        } else if (error_status == 423) {
            // Bloqueo indefinido: requiere desbloqueo manual por administrador.
            let res
            try { res = JSON.parse(response.request.response) } catch (_) {
                return {"message": "Tu cuenta ha sido bloqueada por razones de seguridad. Comunícate con el administrador de tu organización.", "error_keys": null, "lockout_type": "indefinite", "lockout_minutes": null}
            }
            return {
                "message": res.result?.message ?? "Tu cuenta ha sido bloqueada por razones de seguridad. Comunícate con el administrador de tu organización.",
                "error_keys": null,
                "lockout_type": "indefinite",
                "lockout_minutes": null,
            };

        } else if (error_status == 413) {
            // HCSO-2891: nginx corta la petición ANTES de que llegue a Laravel
            // (client_max_body_size), así que el cuerpo no es JSON de la API.
            // Antes caía en el "else" con el texto genérico y el usuario no
            // tenía forma de saber que el problema era el peso del archivo.
            return {
                "message": "El archivo supera el tamaño permitido por el servidor.",
                "error_keys": null,
                "error_code": "PAYLOAD_TOO_LARGE",
            };

        } else if (error_status >= 500) {
            // HCSO-2891: los 5xx se distinguen del error de red. Si la API mandó
            // un envelope con `error_code` (rama de fallo de foto de perfil), se
            // propaga para que la pantalla pueda mapearlo a su propio texto.
            let res
            try { res = JSON.parse(response.request.response) } catch (_) { res = null }

            const error_code = res?.result?.error_code ?? null

            return {
                // El `message` del servidor solo se muestra cuando el endpoint se
                // adhirió al contrato nuevo, es decir cuando mandó `error_code`:
                // ese texto viene de lang/ y está pensado para el usuario.
                //
                // Sin `error_code` se usa el texto genérico A PROPÓSITO. Este util
                // lo importan 118 módulos y hay endpoints que responden 500 con el
                // mensaje crudo de la excepción (p. ej. DiagnosticController:
                // 'Error interno del servidor: ' . $e->getMessage()). Propagarlo
                // filtraría internals al usuario —justo lo contrario de lo que
                // persigue este ticket— y además los consumidores lo pintan con
                // `html:` en swal.
                "message": error_code ? res.result.message : "El servidor no pudo completar la operación, inténtalo de nuevo en unos minutos.",
                "error_keys": null,
                "error_code": error_code,
            };

        } else {
            return {"message": "No hemos podido procesar tu solicitud, inténtalo nuevamente.", "error_keys": null};
        }

    } else {

        // HCSO-2891: sin `response` no hubo respuesta del servidor: es un error
        // de red / timeout, no un fallo de la API. Mensaje distinguible.
        console.log(http_request)
        return {
            "message": "No pudimos contactar al servidor. Revisa tu conexión e inténtalo nuevamente.",
            "error_keys": null,
            "error_code": "NETWORK_ERROR",
        };

    }

}


// Forma que devuelve el catch de toda acción del store que la use. Centralizarla evita que las
// acciones lean `ex.response.data`, que no existe en un error de red y lanzaba dentro del propio
// catch.
export function buildErrorResult(http_request) {

    const decoded = errorHttpMessage(http_request);

    return {
        message: decoded.message,
        error_keys: decoded.error_keys,
        data: http_request?.response?.data ?? [],
        error: true,
        // Sin response el servidor nunca contestó: la petición no salió o no volvió (red caída,
        // DNS, timeout, petición abortada). El autoguardado lo necesita para distinguir un fallo
        // transitorio, que se reintenta solo, de un rechazo que no se resuelve reintentando.
        is_network_error: !http_request?.response,
        // HCSO-2708: código HTTP de la respuesta (null si no la hubo). El autoguardado lo usa
        // para distinguir un 403 de estatus del colaborador, que detiene el envío para siempre,
        // de un 422 de validación. Aditivo: ningún consumidor previo lee esta clave.
        status: http_request?.response?.status ?? null,
    };

}


export function setErrorKey(key_with_error) {

    let error_keys = {};

    if (key_with_error != undefined) {
        key_with_error.forEach((element) => {
            error_keys[element] = true;
            if (element.startsWith('vital_signs.')) {
                error_keys[element.replace('vital_signs.', '')] = true;
            }
        });
    }

    //console.log(error_keys);

    return error_keys;
}
